// Pure helpers for meeting recordings (unit-tested in recording.test.ts).

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Gallery layout: `n` tiles of 16:9 as large as possible in a width × height frame, centred,
 * with `gap` pixels between them (like the gallery view of the meeting).
 */
export function galleryLayout(n: number, width: number, height: number, gap = 8): Rect[] {
  if (n <= 0) return [];
  let best: { cols: number; rows: number; w: number; h: number } | null = null;
  for (let cols = 1; cols <= n; cols++) {
    const rows = Math.ceil(n / cols);
    let w = (width - gap * (cols + 1)) / cols;
    let h = (w * 9) / 16;
    if (h * rows + gap * (rows + 1) > height) {
      h = (height - gap * (rows + 1)) / rows;
      w = (h * 16) / 9;
    }
    if (!best || w > best.w) best = { cols, rows, w, h };
  }
  const { cols, rows, w, h } = best!;
  const rects: Rect[] = [];
  const top = (height - (rows * h + (rows - 1) * gap)) / 2;
  for (let i = 0; i < n; i++) {
    const row = Math.floor(i / cols);
    const inRow = row === rows - 1 ? n - row * cols : cols;
    const left = (width - (inRow * w + (inRow - 1) * gap)) / 2;
    const col = i - row * cols;
    rects.push({ x: Math.round(left + col * (w + gap)), y: Math.round(top + row * (h + gap)), w: Math.round(w), h: Math.round(h) });
  }
  return rects;
}

/** Shared content large on the left, up to `strip` cameras stacked on the right. */
export function shareLayout(width: number, height: number, strip: number, gap = 8): { share: Rect; tiles: Rect[] } {
  const stripW = strip > 0 ? Math.round(width * 0.2) : 0;
  const share = { x: gap, y: gap, w: width - stripW - gap * (strip > 0 ? 3 : 2), h: height - gap * 2 };
  const tileH = Math.round((stripW * 9) / 16);
  const tiles: Rect[] = [];
  const total = strip * tileH + (strip - 1) * gap;
  const top = Math.max(gap, Math.round((height - total) / 2));
  for (let i = 0; i < strip; i++) tiles.push({ x: width - stripW - gap, y: top + i * (tileH + gap), w: stripW, h: tileH });
  return { share, tiles };
}

/**
 * Most universal format the browser can record. MP4 with H.264 (baseline profile) and AAC plays
 * everywhere: QuickTime, Photos on iPhone/Mac, Windows, Android, WhatsApp, editors. Chrome, Edge,
 * Safari and the desktop app record it directly; Firefox only records WebM.
 */
export function pickRecordingType(isSupported: (type: string) => boolean): { mimeType: string; extension: 'webm' | 'mp4' } | null {
  for (const mimeType of [
    'video/mp4;codecs=avc1.42E01F,mp4a.40.2',
    'video/mp4;codecs=avc1,mp4a.40.2',
    'video/mp4',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
  ]) {
    if (isSupported(mimeType)) return { mimeType, extension: mimeType.startsWith('video/mp4') ? 'mp4' : 'webm' };
  }
  return null;
}

/** «Nexo Meet - Reunión de equipo - 2026-10-17 18.05.webm» (no characters that file systems reject). */
export function recordingFileName(title: string, startedAt: Date, extension: string): string {
  const clean = title.replace(/[\\/:*?"<>|\u0000-\u001f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80) || 'Reunión';
  const p = (n: number) => String(n).padStart(2, '0');
  const stamp = `${startedAt.getFullYear()}-${p(startedAt.getMonth() + 1)}-${p(startedAt.getDate())} ${p(startedAt.getHours())}.${p(startedAt.getMinutes())}`;
  return `Nexo Meet - ${clean} - ${stamp}.${extension}`;
}

export function formatClock(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const p = (n: number) => String(n).padStart(2, '0');
  return s >= 3600 ? `${Math.floor(s / 3600)}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)}` : `${p(Math.floor(s / 60))}:${p(s % 60)}`;
}
